import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Receipt, PaperPlaneTilt, CheckCircle, X, Trash, UploadSimple, ArrowsCounterClockwise, Warning, CurrencyInr, Sparkle } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCompany } from "../../context/CompanyContext";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import PaymentModal, { PaymentStatusPill } from "../../components/PaymentModal";
import PurchaseAutoFetchModal from "../../components/PurchaseAutoFetchModal";

const TYPES = ["cash","bank","journal","contra"];
const CATEGORIES = ["Travel","Office Supplies","Utilities","Rent","Salaries","Marketing","Professional Fees","Vendor Payment","General"];
const STATUS_STYLE = {
  draft:    { c:"#717680", bg:"rgba(113,118,128,0.12)", label:"DRAFT" },
  submitted:{ c:"#F4A261", bg:"rgba(244,162,97,0.12)", label:"AT HR" },
  hr_approved:{ c:"#3B82F6", bg:"rgba(59,130,246,0.12)", label:"AT MUKESH" },
  approved: { c:"#0D9488", bg:"rgba(13,148,136,0.12)", label:"APPROVED" },
  rejected: { c:"#D90429", bg:"rgba(217,4,41,0.12)",  label:"REJECTED" },
};
const empty = { vendor:"", amount:0, category:"General", voucher_type:"cash", description:"" };

export default function Vouchers() {
  const { user } = useAuth();
  const { activeId } = useCompany();
  const isHR = user?.roles?.includes("hr") || user?.roles?.includes("admin");
  const isFinal = user?.roles?.includes("admin") || (user?.email||"").toLowerCase() === "mukesh.panchal@shelvestech.com";

  const [tab, setTab] = useState("mine"); // mine | hr | final
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [billFile, setBillFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionVch, setActionVch] = useState(null); // {voucher, kind: 'hr_approve'|'final_approve'|'reject'}
  const [actionNote, setActionNote] = useState("");
  const [payTarget, setPayTarget] = useState(null);
  const [paySummaries, setPaySummaries] = useState({});
  const [fetchOpen, setFetchOpen] = useState(false);
  const [autoFetchedBanner, setAutoFetchedBanner] = useState(null);

  const load = async () => {
    const q = tab==="mine" ? "?mine=true" : tab==="hr" ? "?queue=hr" : tab==="final" ? "?queue=final" : "";
    const { data } = await api.get(`/hr/vouchers${q}`);
    setList(data);
    const summaries = {};
    await Promise.all((data || []).map(async (v) => {
      try {
        const { data: s } = await api.get("/finance/payments", { params: { parent_type: "voucher", parent_id: v.id } });
        summaries[v.id] = s;
      } catch { /* ignore */ }
    }));
    setPaySummaries(summaries);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const create = async () => {
    if (!form.vendor.trim()) { toast.error("Vendor required"); return; }
    if (!form.amount || Number(form.amount)<=0) { toast.error("Amount required"); return; }
    setSubmitting(true);
    try {
      const { data: v } = await api.post("/hr/vouchers", { ...form, amount: Number(form.amount), company_id: activeId || "" });
      if (billFile) {
        const fd = new FormData(); fd.append("file", billFile);
        await api.post(`/hr/vouchers/${v.id}/bill`, fd, { headers:{ "Content-Type":"multipart/form-data" } });
      }
      toast.success("Voucher saved as DRAFT · review then submit");
      setOpen(false); setForm(empty); setBillFile(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSubmitting(false); }
  };

  const submit = async (v) => {
    if (!window.confirm(`Submit voucher to HR for ₹${Number(v.amount).toLocaleString("en-IN")}?`)) return;
    try { await api.post(`/hr/vouchers/${v.id}/submit`); toast.success("Sent to HR"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
  };

  const remove = async (v) => {
    if (!window.confirm("Delete this draft voucher?")) return;
    await api.delete(`/hr/vouchers/${v.id}`);
    setList((curr) => curr.filter((x) => x.id !== v.id));
    toast.success("Deleted");
  };

  const submitAction = async () => {
    if (!actionVch) return;
    const { voucher, kind } = actionVch;
    const url = kind==="hr_approve" ? `/hr/vouchers/${voucher.id}/hr-approve` : kind==="final_approve" ? `/hr/vouchers/${voucher.id}/final-approve` : `/hr/vouchers/${voucher.id}/reject`;
    try {
      await api.post(url, { note: actionNote });
      toast.success(kind==="reject" ? "Voucher rejected · back to submitter" : kind==="hr_approve" ? "Forwarded to Mukesh" : "Final approved · posted to Operating Expenses");
      setActionVch(null); setActionNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const stats = useMemo(() => ({
    drafts: list.filter(v => v.status==="draft").length,
    submitted: list.filter(v => v.status==="submitted").length,
    hr_approved: list.filter(v => v.status==="hr_approved").length,
    approved: list.filter(v => v.status==="approved").length,
  }), [list]);

  return (
    <Layout title="Vouchers" subtitle="Draft → HR → Mukesh → Operating Expenses"
      actions={<>
        <button onClick={()=>setFetchOpen(true)} data-testid="vch-auto-fetch-button" className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white"><Sparkle size={14} weight="bold"/> Auto-Fetch from Bill</button>
        <button onClick={()=>{setForm(empty); setBillFile(null); setAutoFetchedBanner(null); setOpen(true);}} data-testid="new-voucher-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]"><Receipt size={16} weight="bold"/> New Voucher</button>
      </>}>
      <div className="flex gap-2 mb-5">
        {[{k:"mine",l:"My Vouchers"}, isHR && {k:"hr",l:`HR Queue (${stats.submitted})`}, isFinal && {k:"final",l:`Mukesh Queue`}, {k:"all",l:"All"}].filter(Boolean).map(t => (
          <button key={t.k} onClick={()=>setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-3 py-1.5 text-xs font-mono-shelv uppercase tracking-widest border ${tab===t.k?"bg-[#0A0A0A] text-white border-[#0A0A0A]":"border-black/15 hover:border-[#0A0A0A]"}`}>
            {t.l}
          </button>
        ))}
      </div>
      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="vouchers-table">
          <thead className="bg-[#F5F6F8] text-left"><tr>{["Vendor","Amount","Type","Category","Status","Submitter","Last update",""].map(h => <th key={h} className="px-4 py-3 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>)}</tr></thead>
          <tbody>
            {list.length===0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-[#717680]">No vouchers here.</td></tr>}
            {list.map(v => {
              const s = STATUS_STYLE[v.status] || STATUS_STYLE.draft;
              const isMine = v.user_id === user?.user_id;
              return (
                <tr key={v.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`vch-row-${v.id}`}>
                  <td className="px-4 py-3"><div className="font-semibold">{v.vendor}</div><div className="text-[10px] text-[#717680] line-clamp-1">{v.description}</div></td>
                  <td className="px-4 py-3 font-mono-shelv">₹{Number(v.amount).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 uppercase text-[11px] font-mono-shelv">{v.voucher_type}</td>
                  <td className="px-4 py-3 text-[#4A4D57]">{v.category}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-1 text-[10px] font-mono-shelv tracking-widest font-bold border" style={{ background: s.bg, color: s.c, borderColor: s.c+"40" }}>{s.label}</span>
                    {v.rejected_review && <div className="text-[10px] text-[#D90429] mt-1 italic">{v.rejected_review.note || "rejected"}</div>}
                  </td>
                  <td className="px-4 py-3 text-[#4A4D57] text-xs">{v.submitter_name || v.submitter_email}</td>
                  <td className="px-4 py-3 text-xs text-[#717680] font-mono-shelv">{(v.updated_at||"").slice(0,16).replace("T"," ")}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {paySummaries[v.id] && <span className="mr-2"><PaymentStatusPill status={paySummaries[v.id].status} /></span>}
                    {(v.status==="approved" || v.status==="hr_approved" || v.status==="final_approved") && <button onClick={()=>setPayTarget(v)} className="text-[11px] px-2 py-1 border border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488] hover:text-white mr-1" data-testid={`pay-vch-${v.id}`}><CurrencyInr size={11} weight="bold" className="inline mr-0.5"/>Pay</button>}
                    {v.status==="draft" && isMine && <button onClick={()=>submit(v)} className="text-[11px] px-2 py-1 bg-[#0A0A0A] text-white mr-1 hover:bg-[#D90429]" data-testid={`submit-${v.id}`}><PaperPlaneTilt size={11} weight="bold" className="inline mr-0.5"/>Submit</button>}
                    {v.status==="draft" && isMine && <button onClick={()=>remove(v)} className="text-[11px] p-1 text-[#D90429]" data-testid={`del-${v.id}`}><Trash size={12}/></button>}
                    {v.status==="submitted" && isHR && <>
                      <button onClick={()=>{setActionVch({voucher:v,kind:"hr_approve"}); setActionNote("");}} className="text-[11px] px-2 py-1 bg-[#0D9488] text-white mr-1 hover:bg-[#0F766E]" data-testid={`hr-approve-${v.id}`}><CheckCircle size={11} weight="bold" className="inline mr-0.5"/>Approve →</button>
                      <button onClick={()=>{setActionVch({voucher:v,kind:"reject"}); setActionNote("");}} className="text-[11px] px-2 py-1 bg-[#D90429] text-white" data-testid={`hr-reject-${v.id}`}><X size={11} weight="bold" className="inline"/></button>
                    </>}
                    {v.status==="hr_approved" && isFinal && <>
                      <button onClick={()=>{setActionVch({voucher:v,kind:"final_approve"}); setActionNote("");}} className="text-[11px] px-2 py-1 bg-[#0D9488] text-white mr-1 hover:bg-[#0F766E]" data-testid={`final-approve-${v.id}`}><CheckCircle size={11} weight="bold" className="inline mr-0.5"/>Final</button>
                      <button onClick={()=>{setActionVch({voucher:v,kind:"reject"}); setActionNote("");}} className="text-[11px] px-2 py-1 bg-[#D90429] text-white" data-testid={`final-reject-${v.id}`}><X size={11} weight="bold" className="inline"/></button>
                    </>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create voucher dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none">
          <DialogHeader><DialogTitle className="font-display tracking-tight">New voucher</DialogTitle></DialogHeader>
          {autoFetchedBanner && (
            <div className="border border-[#0D9488]/30 bg-[#0D9488]/5 px-3 py-2 text-xs flex flex-wrap items-center gap-3" data-testid="vch-autofetch-banner">
              <Sparkle size={14} weight="bold" className="text-[#0D9488]"/>
              <span className="font-semibold text-[#0A0A0A]">Auto-fetched from vendor bill</span>
              <span className="font-mono-shelv text-[#4A4D57]">Vendor: <b>{autoFetchedBanner.vendor}</b>{autoFetchedBanner.created_new && <span className="ml-1 text-[10px] px-1 py-0.5 bg-[#0D9488] text-white">NEW</span>}</span>
              {autoFetchedBanner.bill_no && <span className="font-mono-shelv text-[#4A4D57]">Bill #: <b>{autoFetchedBanner.bill_no}</b></span>}
              {autoFetchedBanner.grand_total > 0 && <span className="font-mono-shelv text-[#4A4D57]">Total: <b>₹{Number(autoFetchedBanner.grand_total).toLocaleString("en-IN")}</b></span>}
              {autoFetchedBanner.items > 0 && <span className="font-mono-shelv text-[#4A4D57]">{autoFetchedBanner.items} item(s)</span>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Vendor / Payee *</Label><Input value={form.vendor} onChange={e=>setForm({...form,vendor:e.target.value})} className="rounded-none" data-testid="vch-vendor"/></div>
            <div><Label>Amount (₹) *</Label><Input type="number" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} className="rounded-none" data-testid="vch-amount"/></div>
            <div><Label>Voucher Type</Label>
              <Select value={form.voucher_type} onValueChange={v=>setForm({...form,voucher_type:v})}><SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent>{TYPES.map(t=><SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={v=>setForm({...form,category:v})}><SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Description / purpose</Label><Textarea rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} className="rounded-none"/></div>
            <div className="col-span-2">
              <Label>Bill / Receipt (optional)</Label>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={e=>setBillFile(e.target.files?.[0]||null)} className="block w-full text-sm" data-testid="vch-bill-input"/>
              {billFile && <div className="text-[10px] text-[#717680] mt-1">{billFile.name}</div>}
            </div>
          </div>
          <DialogFooter>
            <button onClick={()=>setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={create} disabled={submitting} data-testid="save-vch-draft" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50">{submitting?"Saving…":"Save as Draft"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval / rejection note dialog */}
      <Dialog open={!!actionVch} onOpenChange={(o)=>{ if (!o){ setActionVch(null); setActionNote(""); } }}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader><DialogTitle className="font-display tracking-tight">
            {actionVch?.kind==="reject" ? "Reject voucher" : actionVch?.kind==="final_approve" ? "Final approval — post to Expenses" : "Forward to Mukesh"}
          </DialogTitle></DialogHeader>
          {actionVch && (
            <div className="space-y-3 text-sm">
              <div className="border border-black/10 bg-[#FAFBFC] p-3 space-y-1">
                <div><span className="text-[#717680] text-xs">Vendor</span><div className="font-bold">{actionVch.voucher.vendor}</div></div>
                <div><span className="text-[#717680] text-xs">Amount</span><div className="font-mono-shelv font-bold text-[#D90429]">₹{Number(actionVch.voucher.amount).toLocaleString("en-IN")}</div></div>
                <div><span className="text-[#717680] text-xs">Submitter</span><div>{actionVch.voucher.submitter_name || actionVch.voucher.submitter_email}</div></div>
              </div>
              <div><Label>Note {actionVch.kind==="reject" && "(required)"}</Label>
                <Textarea rows={3} value={actionNote} onChange={e=>setActionNote(e.target.value)} className="rounded-none" data-testid="action-note-input"/>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={()=>{setActionVch(null); setActionNote("");}} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={submitAction} disabled={actionVch?.kind==="reject" && !actionNote.trim()} data-testid="action-submit"
              className={`px-4 py-2 text-white text-sm font-semibold disabled:opacity-50 ${actionVch?.kind==="reject"?"bg-[#D90429] hover:bg-[#EF233C]":"bg-[#0D9488] hover:bg-[#0F766E]"}`}>
              {actionVch?.kind==="reject" ? "Reject" : actionVch?.kind==="final_approve" ? "Approve & Post" : "Forward to Mukesh"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PaymentModal
        open={!!payTarget}
        onOpenChange={(o) => !o && setPayTarget(null)}
        parentType="voucher"
        parentId={payTarget?.id}
        parentLabel={payTarget ? `${payTarget.vendor} · ₹${Number(payTarget.amount).toLocaleString("en-IN")}` : ""}
        onUpdated={(s) => setPaySummaries((prev) => ({ ...prev, [payTarget.id]: s }))}
      />

      <PurchaseAutoFetchModal
        open={fetchOpen}
        onOpenChange={setFetchOpen}
        companyId={activeId}
        target="voucher"
        onParsed={(data) => {
          const p = data.voucher_prefill || {};
          setForm({
            ...empty,
            vendor: p.vendor || "",
            amount: p.amount || 0,
            category: CATEGORIES.includes(p.category) ? p.category : "Vendor Payment",
            voucher_type: TYPES.includes(p.voucher_type) ? p.voucher_type : "bank",
            description: p.description || "",
          });
          setBillFile(null);
          setAutoFetchedBanner({
            vendor: data.vendor?.name,
            bill_no: data.bill?.bill_no,
            grand_total: data.bill?.grand_total,
            items: (data.bill?.items || []).length,
            created_new: data.vendor?.created_new,
          });
          setOpen(true);
        }}
      />
    </Layout>
  );
}
